/**
 * Iconos del mockup, portados tal cual.
 *
 * Son los mismos trazos que `mockup/admin.html` dibuja con su registro `icons`:
 * una sola familia de línea, 24×24, sin relleno y con el color heredado del
 * texto. Se declaran aquí una vez para que el panel y el portal no dibujen dos
 * versiones distintas del mismo símbolo.
 */
export type IconName =
  | 'home'
  | 'clipboard'
  | 'pizza'
  | 'users'
  | 'receipt'
  | 'message'
  | 'settings'
  | 'check'
  | 'bell'
  | 'plus'
  | 'alert'
  | 'refresh'
  | 'clock'
  | 'send'
  | 'x'
  | 'boxes'
  | 'user'
  | 'pencil'
  | 'menu'
  | 'search'
  | 'chevron'
  | 'logout'
  | 'eye'
  | 'grid'
  | 'download'
  | 'key'
  | 'power'
  | 'list'
  | 'eyeOff';

/** Trazos de cada icono, copiados del mockup para no inventar un dibujo nuevo. */
const ICONS: Record<IconName, JSX.Element> = {
  home: <><path d="m3 10 9-7 9 7" /><path d="M5 9v11h14V9" /><path d="M9 20v-6h6v6" /></>,
  clipboard: (
    <>
      <rect width="14" height="18" x="5" y="3" rx="2" />
      <path d="M9 3V1h6v2M9 9h6M9 13h6M9 17h4" />
    </>
  ),
  pizza: (
    <>
      {/* Porción: punta arriba, borde curvo abajo. El arco es la orilla, la
          línea interior el reborde de masa y los puntos los ingredientes. */}
      <path d="M12 3 4.7 18.6a12 12 0 0 0 14.6 0L12 3Z" />
      <path d="M6.2 15.5a13 13 0 0 1 11.6 0" />
      <path d="M11 9.4h.01M14.3 12.2h.01M9.9 12.9h.01" />
    </>
  ),
  users: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  receipt: (
    <>
      <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 1 .7V2l-3 2-3-2-3 2-3-2-3 2-3-2Z" />
      <path d="M16 8h-6M16 12h-6M13 16h-3" />
    </>
  ),
  message: (
    <>
      <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />
      <path d="M8 10h.01M12 10h.01M16 10h.01" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21h-4v-.1A1.7 1.7 0 0 0 8.6 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1H3v-4h1a1.7 1.7 0 0 0 .6-1 1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6V3h4v1a1.7 1.7 0 0 0 1 .6 1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 9c.13.37.35.7.6 1h1v4h-1c-.25.3-.47.63-.6 1Z" />
    </>
  ),
  check: <path d="m20 6-11 11-5-5" />,
  bell: (
    <>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  alert: (
    <>
      <path d="M10.3 2.9 1.8 17a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 2.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
  refresh: <path d="M20 11a8 8 0 1 0-2.3 5.7L20 14M20 4v7h-7" />,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  send: <><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></>,
  x: <path d="M18 6 6 18M6 6l12 12" />,
  // Cajas apiladas: el stock no tiene icono propio en el mockup, así que se
  // dibuja con la misma familia de línea que el resto.
  boxes: (
    <>
      <path d="M3 8.5 12 4l9 4.5-9 4.5-9-4.5Z" />
      <path d="m3 13 9 4.5 9-4.5" />
      <path d="m3 17.5 9 4.5 9-4.5" />
    </>
  ),
  user: <><circle cx="12" cy="8" r="3.5" /><path d="M5 20a7 7 0 0 1 14 0" /></>,
  // El mockup no trae lápiz —nunca tuvo edición por fila—, así que se dibuja
  // uno con el mismo grosor y remate que el resto de la familia.
  menu: <path d="M4 6h16M4 12h16M4 18h16" />,
  search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>,
  chevron: <path d="m7 9.5 5 5 5-5" />,
  logout: (
    <>
      <path d="M10 17l5-5-5-5M15 12H3" />
      <path d="M14 3h5a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-5" />
    </>
  ),
  key: <><circle cx="8" cy="15" r="4" /><path d="m10.8 12.2 8.7-8.7M16 7l3 3M14 9l2 2" /></>,
  power: <><path d="M12 3v8" /><path d="M6.4 6.6a8 8 0 1 0 11.2 0" /></>,
  download: <><path d="M12 4v11M7 10l5 5 5-5" /><path d="M5 20h14" /></>,
  grid: <><rect width="7" height="7" x="3" y="3" rx="1.5" /><rect width="7" height="7" x="14" y="3" rx="1.5" /><rect width="7" height="7" x="3" y="14" rx="1.5" /><rect width="7" height="7" x="14" y="14" rx="1.5" /></>,
  list: <path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" />,
  // Tampoco trae ojo: se dibuja con el mismo trazo para mostrar la contraseña.
  eye: (
    <>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  eyeOff: (
    <>
      <path d="M10.6 5.1A10.5 10.5 0 0 1 12 5c6.4 0 10 7 10 7a17.6 17.6 0 0 1-2.7 3.6" />
      <path d="M6.6 6.6A17.4 17.4 0 0 0 2 12s3.6 7 10 7a9.7 9.7 0 0 0 5.4-1.6" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="m3 3 18 18" />
    </>
  ),
  pencil: (
    <>
      <path d="M4 20h4l10-10a2.8 2.8 0 0 0-4-4L4 16v4Z" />
      <path d="m13.5 6.5 4 4" />
    </>
  ),
};

export function Icon({ name, size = 19 }: { name: IconName; size?: number }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      style={{
        flex: '0 0 auto',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 1.8,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
      }}
    >
      {ICONS[name]}
    </svg>
  );
}
